import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Printer, ShieldAlert, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Seo from '../components/Seo';
import { mockData } from '../mock';
import PaymentOptions from '../components/PaymentOptions';
import { trackContactFormSubmit } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '', // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // prevent double-submit

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/inquiries/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          subject: formData.subject || null,
          message: formData.message.trim(),
          source_page: '/contact',
          website: formData.website || null, // honeypot
        }),
      });

      if (res.status === 429) {
        toast.error('Too many submissions from your network. Please wait a few minutes and try again, or call 832-925-2128.');
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Your message could not be saved. Please try again or call 832-925-2128.');
        return;
      }

      const data = await res.json();
      // Server confirms save. Distinguish 3 states.
      if (data.status === 'received' && data.email_status === 'sent') {
        trackContactFormSubmit({ source_page: 'contact', status: 'received' });
        toast.success(`Message received (Ref ${data.ref}). We'll reply to ${formData.email.trim()} within one business day.`);
      } else if (data.status === 'received_email_failed') {
        trackContactFormSubmit({ source_page: 'contact', status: 'received_email_failed' });
        toast.warning(`Message saved (Ref ${data.ref}), but our automated email couldn't be delivered. Please call 832-925-2128 to confirm receipt.`);
      } else {
        trackContactFormSubmit({ source_page: 'contact', status: data.status || 'received' });
        toast.success(`Message received (Ref ${data.ref}).`);
      }

      setSubmitted({
        ref: data.ref,
        overall: data.status,
        email_status: data.email_status,
        contact_email: formData.email.trim(),
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', website: '' });
    } catch (_err) {
      toast.error('Your message could not be saved. Please try again or call 832-925-2128.');
    } finally {
      setSubmitting(false);
    }
  };

  const serviceAreas = [
    "Houston",
    "Richmond",
    "Sugar Land",
    "Katy",
    "Missouri City",
    "Stafford / Rosenberg",
    "Fort Bend & Harris Counties",
    "Extended-distance by advance arrangement"
  ];

  return (
    <div className="min-h-screen pt-20">
      <Seo
        title="Contact MoveraCare Mobility | Houston NEMT Dispatch"
        description="Contact MoveraCare Mobility for non-emergency medical transportation across Greater Houston. Toll-free (832) 925-2128 · Local (832) 925-2128 · dispatch@moveracaremobility.com"
        path="/contact"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Contact', url: '/contact' }]}
      />
      {/* Hero Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Get in touch with MoveraCare Mobility for all your medical transportation needs
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Information Cards */}
          <Card className="border-2 border-yellow-400">
            <CardHeader>
              <Phone className="w-10 h-10 text-yellow-400 mb-4" />
              <CardTitle>Phone</CardTitle>
              <CardDescription>Speak with our team</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Main Line:</p>
                <a href={"tel:+18329252128"} className="text-lg font-semibold text-black hover:text-yellow-400 transition-colors">
                  {mockData.company.phone}
                </a>
              </div>
              <div>
                <p className="text-sm text-gray-600">Dispatch:</p>
                <a href={"tel:+18329252128"} className="text-lg font-semibold text-black hover:text-yellow-400 transition-colors">
                  {mockData.company.dispatchPhone}
                </a>
              </div>
              <div className="pt-2 border-t border-gray-100 mt-2">
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <Printer className="w-3.5 h-3.5" />
                  Fax:
                </p>
                <p className="text-lg font-semibold text-black" data-testid="contact-fax">
                  {mockData.company.fax}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-400">
            <CardHeader>
              <Mail className="w-10 h-10 text-yellow-400 mb-4" />
              <CardTitle>Email</CardTitle>
              <CardDescription>Send us a message</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">General Inquiries:</p>
                <a href={`mailto:${mockData.company.email}`} className="text-black hover:text-yellow-400 transition-colors font-medium">
                  {mockData.company.email}
                </a>
              </div>
              <div className="pt-2">
                <p className="text-sm text-gray-600">Dispatch:</p>
                <a href="mailto:dispatch@moveracaremobility.com" className="text-black hover:text-yellow-400 transition-colors font-medium">
                  dispatch@moveracaremobility.com
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-400">
            <CardHeader>
              <Clock className="w-10 h-10 text-yellow-400 mb-4" />
              <CardTitle>Hours</CardTitle>
              <CardDescription>When we're available</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <p className="font-semibold text-black">Dispatch &amp; Office:</p>
                  <p className="text-sm text-gray-600">Monday–Saturday: 6:00 AM–7:00 PM</p>
                  <p className="text-sm text-gray-600">Sunday: 12:00 PM–5:00 PM</p>
                </div>
                <div className="pt-2">
                  <p className="text-xs text-gray-500 italic">Trips outside standard hours may be considered by advance arrangement. For medical emergencies, call 911.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted && (
                  <div
                    className={`mb-6 rounded-xl border-2 p-5 ${
                      submitted.overall === 'received'
                        ? 'border-[#F9A825] bg-[#FFF8E1]'
                        : 'border-orange-400 bg-orange-50'
                    }`}
                    data-testid="contact-success-card"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        submitted.overall === 'received' ? 'bg-[#F9A825]' : 'bg-orange-500'
                      }`}>
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-bold tracking-wider text-[#004C97] mb-1" data-testid="contact-ref">
                          MESSAGE RECEIVED · REF #{submitted.ref}
                        </p>
                        {submitted.overall === 'received' ? (
                          <p className="text-sm text-gray-700 leading-relaxed">
                            Thank you — we&apos;ll reply to <strong>{submitted.contact_email}</strong> within one business day.
                            Please keep your reference number for follow-up.
                          </p>
                        ) : (
                          <p className="text-sm text-gray-800 leading-relaxed">
                            Your message was saved, but our automated email couldn&apos;t be delivered.
                            Please call <a href="tel:+18329252128" className="font-bold underline">832-925-2128</a> and reference <strong>#{submitted.ref}</strong>.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-6 rounded-lg border-l-4 border-red-500 bg-red-50 p-4 flex items-start gap-3" data-testid="contact-phi-warning">
                  <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-900 mb-1">Please do not include protected health information (PHI).</p>
                    <p className="text-xs text-red-800 leading-relaxed">
                      Do not share patient names, dates of birth, medical conditions, insurance IDs, or trip specifics in this general message.
                      To request transportation, use the <a href="/book" className="underline font-semibold">secure booking form</a>. For medical emergencies, call 911.
                    </p>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Your name"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="your.email@example.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <Select value={formData.subject} onValueChange={(value) => handleChange('subject', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="booking">Booking Inquiry</SelectItem>
                          <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                          <SelectItem value="billing">Billing Question</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="How can we help you? (Please do not include patient or medical details.)"
                      rows={6}
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  {/* Honeypot — hidden from real users, accessible for tests via data-testid */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                    <label htmlFor="website-hp">Website (leave blank)</label>
                    <input
                      id="website-hp"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                      data-testid="contact-honeypot"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full bg-yellow-400 text-black hover:bg-yellow-500 font-semibold disabled:opacity-70"
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <MapPin className="w-10 h-10 text-yellow-400 mb-4" />
                <CardTitle>Service Areas</CardTitle>
                <CardDescription>Greater Houston &amp; Fort Bend County</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {serviceAreas.map((area, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-gray-700">{area}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-black text-white">
              <CardHeader>
                <CardTitle>Headquarters</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-2">{mockData.company.address}</p>
                <p className="text-sm text-gray-400">
                  Serving healthcare facilities and patients across Greater Houston
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-yellow-400">
              <CardHeader>
                <CardTitle>Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a href="/book" className="block text-black hover:text-yellow-400 transition-colors font-medium">
                  → Book a Ride
                </a>
                <a href="/partners" className="block text-black hover:text-yellow-400 transition-colors font-medium">
                  → Become a Partner
                </a>
                <a href="/careers" className="block text-black hover:text-yellow-400 transition-colors font-medium">
                  → Join Our Team
                </a>
                <a href="/services" className="block text-black hover:text-yellow-400 transition-colors font-medium">
                  → View Services
                </a>
              </CardContent>
            </Card>

            <PaymentOptions variant="compact" />
          </div>
        </div>

        {/* Google Map — Houston / Fort Bend / Harris County service area */}
        <Card className="mt-12 overflow-hidden border-2 border-gray-100 shadow-md" data-testid="service-area-map-card">
          <CardHeader className="bg-[#004C97] text-white">
            <CardTitle className="flex items-center gap-2 text-white">
              <MapPin className="w-5 h-5 text-[#F9A825]" />
              Our Service Area
            </CardTitle>
            <CardDescription className="text-gray-200">
              Richmond, TX • Houston Metro • Fort Bend County • Harris County
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative w-full" style={{ paddingBottom: '50%' }}>
              <iframe
                title="MoveraCare Mobility Service Area Map — Richmond, Houston, Fort Bend, Harris County, TX"
                src="https://www.google.com/maps?q=Richmond,+TX&z=10&output=embed"
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="service-area-map-iframe"
              />
            </div>
            <div className="bg-gray-50 px-4 py-3 text-sm text-gray-600 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#F9A825]" />
                Headquartered in Richmond, TX — serving the greater Houston metro area
              </span>
              <a
                href="https://www.google.com/maps?q=Richmond,+TX"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#004C97] font-semibold hover:text-[#F9A825] transition-colors"
                data-testid="service-area-map-link"
              >
                Open in Google Maps →
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
